import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { supabase } from "@/lib/supabase";

export interface AuthUser {
  id: string;
  name: string;
  phone: string;
  email?: string;
  joinedAt: string;
  city?: string;
  address?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  session: null;
  login: (user: AuthUser) => void;
  logout: () => void;
  isLoggedIn: boolean;
  sendOtp: (phone: string) => Promise<{ error: string | null; whatsappUrl?: string; autoSent?: boolean }>;
  verifyOtp: (phone: string, token: string) => Promise<{ error: string | null; isNew: boolean }>;
  loginOrRegisterWithEmail: (email: string, password: string, name?: string) => Promise<{ error: string | null; isNew: boolean; user?: AuthUser }>;
  findOrCreateCustomer: (phone: string, name?: string) => Promise<AuthUser | null>;
  getCustomerByPhone: (phone: string) => Promise<AuthUser | null>;
  updateCustomerName: (phone: string, name: string) => Promise<boolean>;
  updateCustomerAddress: (phone: string, city: string, address: string) => Promise<boolean>;
  updateCustomerEmail: (customerId: string, email: string) => Promise<{ error: string | null }>;
  updateCustomerPhone: (customerId: string, phone: string) => Promise<{ error: string | null }>;
}

// ── Password helpers (SHA-256 via Web Crypto) ──
async function hashPassword(password: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

// ── Phone placeholder for email-only users ──
function makePhonePlaceholder(email: string): string {
  // Always non-empty, always starts with "email_"
  const safe = email.trim().toLowerCase().replace(/[^a-z0-9]/g, "_");
  return `email_${safe}_${Date.now()}`;
}

function isRealPhone(phone: string): boolean {
  return !!phone && !phone.startsWith("email_") && phone.trim() !== "";
}

// ── WhatsApp OTP helpers ──
const OTP_SESSION_KEY = "ebdaa_otp";
const OTP_EXPIRY_MS = 10 * 60 * 1000;

function generateOtp(): string {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

function saveOtp(phone: string, otp: string) {
  sessionStorage.setItem(OTP_SESSION_KEY, JSON.stringify({
    phone,
    otp,
    expiresAt: Date.now() + OTP_EXPIRY_MS,
  }));
}

function verifyStoredOtp(phone: string, token: string): "valid" | "expired" | "invalid" {
  try {
    const raw = sessionStorage.getItem(OTP_SESSION_KEY);
    if (!raw) return "invalid";
    const stored = JSON.parse(raw) as { phone: string; otp: string; expiresAt: number };
    if (stored.phone !== phone) return "invalid";
    if (Date.now() > stored.expiresAt) return "expired";
    if (stored.otp !== token.trim()) return "invalid";
    sessionStorage.removeItem(OTP_SESSION_KEY);
    return "valid";
  } catch {
    return "invalid";
  }
}

function buildWhatsAppOtpUrl(phone: string, otp: string, storeName = "متجر الإبداع"): string {
  let wa = phone.replace(/[\s\-]/g, "");
  if (wa.startsWith("05")) wa = "966" + wa.slice(1);
  else if (wa.startsWith("5")) wa = "966" + wa;
  else if (wa.startsWith("+")) wa = wa.slice(1);
  const msg = `🔐 رمز التحقق الخاص بك في ${storeName}:\n\n*${otp}*\n\nصالح لمدة 10 دقائق — لا تشاركه مع أحد.`;
  return `https://wa.me/${wa}?text=${encodeURIComponent(msg)}`;
}

// ── Map DB row → AuthUser ──
function rowToAuthUser(row: {
  id: string; name: string; phone: string; email?: string | null;
  joined_at: string; city?: string | null; address?: string | null;
}): AuthUser {
  return {
    id: row.id,
    name: row.name,
    phone: isRealPhone(row.phone) ? row.phone : "",
    email: row.email ?? undefined,
    joinedAt: row.joined_at,
    city: row.city ?? undefined,
    address: row.address ?? undefined,
  };
}

const AuthContext = createContext<AuthContextType | null>(null);
const STORAGE_KEY = "ebdaa_auth_user";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // Sync user from Supabase on mount
  useEffect(() => {
    const syncUser = async () => {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;
      try {
        const localUser: AuthUser = JSON.parse(stored);
        let query = supabase.from("customers").select("id, name, phone, email, joined_at, city, address");

        if (isRealPhone(localUser.phone)) {
          query = query.eq("phone", localUser.phone);
        } else if (localUser.email) {
          query = query.eq("email", localUser.email);
        } else if (localUser.id) {
          query = query.eq("id", localUser.id);
        } else {
          return;
        }

        const { data } = await query.maybeSingle();
        if (data) {
          const freshUser = rowToAuthUser(data);
          setUser(freshUser);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(freshUser));
        }
      } catch {
        // keep local data if network fails
      }
    };
    syncUser();
  }, []);

  const login = useCallback((userData: AuthUser) => {
    setUser(userData);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(OTP_SESSION_KEY);
  }, []);

  // ── Send OTP ──
  const sendOtp = useCallback(async (phone: string): Promise<{ error: string | null; whatsappUrl?: string; autoSent?: boolean }> => {
    try {
      const otp = generateOtp();
      saveOtp(phone, otp);

      let otpTemplate: string | undefined;
      try {
        const t = localStorage.getItem("whatsapp_otp_template");
        if (t) otpTemplate = t;
      } catch { /* ignore */ }

      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      if (supabaseUrl && supabaseAnonKey) {
        try {
          const res = await fetch(`${supabaseUrl}/functions/v1/whatsapp-notify`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${supabaseAnonKey}` },
            body: JSON.stringify({ type: "otp", phone, otp, otpTemplate }),
          });
          if (res.ok) {
            const data = await res.json();
            if (data.autoSent) return { error: null, autoSent: true };
            return { error: null, whatsappUrl: buildWhatsAppOtpUrl(phone, otp), autoSent: false };
          }
        } catch { /* fallback */ }
      }

      return { error: null, whatsappUrl: buildWhatsAppOtpUrl(phone, otp), autoSent: false };
    } catch {
      return { error: "حدث خطأ أثناء توليد الرمز، حاول مرة أخرى" };
    }
  }, []);

  // ── Verify OTP ──
  const verifyOtp = useCallback(async (phone: string, token: string): Promise<{ error: string | null; isNew: boolean }> => {
    try {
      const result = verifyStoredOtp(phone, token);
      if (result === "expired") return { error: "انتهت صلاحية الرمز، اطلب رمزاً جديداً", isNew: false };
      if (result === "invalid") return { error: "رمز التحقق غير صحيح، حاول مرة أخرى", isNew: false };

      const { data } = await supabase
        .from("customers")
        .select("id")
        .eq("phone", phone)
        .maybeSingle();

      return { error: null, isNew: !data };
    } catch {
      return { error: "تعذّر الاتصال، تحقق من الإنترنت", isNew: false };
    }
  }, []);

  const getCustomerByPhone = useCallback(async (phone: string): Promise<AuthUser | null> => {
    try {
      if (!isRealPhone(phone)) return null;
      const { data } = await supabase
        .from("customers")
        .select("id, name, phone, email, joined_at, city, address")
        .eq("phone", phone)
        .maybeSingle();
      return data ? rowToAuthUser(data) : null;
    } catch {
      return null;
    }
  }, []);

  // ── Login or Register with Email ──
  const loginOrRegisterWithEmail = useCallback(async (
    email: string,
    password: string,
    name?: string
  ): Promise<{ error: string | null; isNew: boolean; user?: AuthUser }> => {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      if (!normalizedEmail) return { error: "يرجى إدخال بريد إلكتروني صحيح", isNew: false };

      // 1. Check if customer exists by email
      const { data: existing, error: selectError } = await supabase
        .from("customers")
        .select("id, name, phone, email, joined_at, city, address, password_hash")
        .eq("email", normalizedEmail)
        .maybeSingle();

      if (selectError) {
        console.error("Select error:", selectError.message, selectError.code);
        return { error: "تعذّر الاتصال بقاعدة البيانات، حاول مرة أخرى", isNew: false };
      }

      if (existing) {
        // Existing user — verify password
        const inputHash = await hashPassword(password);

        if (!existing.password_hash) {
          // Old account without password — set it now
          await supabase.from("customers").update({
            password_hash: inputHash,
            last_login: new Date().toISOString(),
          }).eq("id", existing.id);
        } else if (existing.password_hash !== inputHash) {
          return { error: "كلمة المرور غير صحيحة، حاول مرة أخرى", isNew: false };
        } else {
          await supabase.from("customers").update({
            last_login: new Date().toISOString(),
          }).eq("id", existing.id);
        }

        return { error: null, isNew: false, user: rowToAuthUser(existing) };
      }

      // 2. New user — need name
      if (!name || name.trim().length < 2) {
        return { error: null, isNew: true };
      }

      const passwordHash = await hashPassword(password);
      // Always generate a unique non-empty phone placeholder
      const phonePlaceholder = makePhonePlaceholder(normalizedEmail);

      // INSERT
      const { error: insertError } = await supabase
        .from("customers")
        .insert({
          email: normalizedEmail,
          phone: phonePlaceholder,
          name: name.trim(),
          password_hash: passwordHash,
          joined_at: new Date().toISOString(),
          last_login: new Date().toISOString(),
        });

      if (insertError) {
        console.error("Insert error:", insertError.message, "code:", insertError.code, "details:", insertError.details, "hint:", insertError.hint);

        if (insertError.code === "23505") {
          // Duplicate — try to fetch existing and login
          const { data: dupUser } = await supabase
            .from("customers")
            .select("id, name, phone, email, joined_at, city, address, password_hash")
            .eq("email", normalizedEmail)
            .maybeSingle();

          if (dupUser) {
            const inputHash = await hashPassword(password);
            if (dupUser.password_hash && dupUser.password_hash !== inputHash) {
              return { error: "هذا البريد الإلكتروني مستخدم بالفعل، جرب تسجيل الدخول", isNew: false };
            }
            return { error: null, isNew: false, user: rowToAuthUser(dupUser) };
          }
          return { error: "هذا البريد الإلكتروني مستخدم بالفعل، جرب تسجيل الدخول", isNew: false };
        }

        // Any other error — show actual error code to help debug
        return { error: `فشل إنشاء الحساب — ${insertError.message || insertError.code || "خطأ غير معروف"}`, isNew: true };
      }

      // Fetch the newly created user by email
      const { data: created } = await supabase
        .from("customers")
        .select("id, name, phone, email, joined_at, city, address")
        .eq("email", normalizedEmail)
        .maybeSingle();

      if (!created) {
        // INSERT succeeded but SELECT returned nothing — build fallback user
        const fallbackUser: AuthUser = {
          id: phonePlaceholder,
          name: name.trim(),
          phone: "",
          email: normalizedEmail,
          joinedAt: new Date().toISOString(),
        };
        return { error: null, isNew: true, user: fallbackUser };
      }

      // Notify admin (fire and forget)
      try {
        const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
        if (supabaseUrl && supabaseAnonKey) {
          fetch(`${supabaseUrl}/functions/v1/whatsapp-notify`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${supabaseAnonKey}` },
            body: JSON.stringify({ type: "new_customer", name: name.trim(), phone: normalizedEmail }),
          }).catch(() => {});
        }
      } catch { /* ignore */ }

      return { error: null, isNew: true, user: rowToAuthUser(created) };
    } catch (err) {
      console.error("loginOrRegisterWithEmail error:", err);
      return { error: "تعذّر الاتصال، تحقق من الإنترنت", isNew: false };
    }
  }, []);

  // ── Find or Create Customer (phone flow) ──
  const findOrCreateCustomer = useCallback(async (phone: string, name?: string): Promise<AuthUser | null> => {
    try {
      if (!isRealPhone(phone)) return null;

      const existing = await getCustomerByPhone(phone);
      if (existing) {
        await supabase.from("customers").update({ last_login: new Date().toISOString() }).eq("phone", phone);
        return existing;
      }

      if (!name || name.trim().length < 2) return null;

      // Double-check before insert
      const { data: dupCheck } = await supabase.from("customers").select("id").eq("phone", phone).maybeSingle();
      if (dupCheck) return getCustomerByPhone(phone);

      // INSERT without .select() to avoid RLS SELECT issue
      const { error: insertErr } = await supabase
        .from("customers")
        .insert({
          phone,
          name: name.trim(),
          joined_at: new Date().toISOString(),
          last_login: new Date().toISOString(),
        });

      if (insertErr) {
        console.error("findOrCreateCustomer insert error:", insertErr.message, insertErr.code);
        // If duplicate, just fetch the existing record
        if (insertErr.code === "23505") {
          return getCustomerByPhone(phone);
        }
        return null;
      }

      // Fetch newly created customer
      const data = await getCustomerByPhone(phone);
      if (!data) {
        // Fallback — return minimal user object
        return {
          id: "",
          name: name.trim(),
          phone,
          joinedAt: new Date().toISOString(),
        };
      }

      // Notify admin (fire and forget)
      try {
        const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
        if (supabaseUrl && supabaseAnonKey) {
          let newCustomerTemplate: string | undefined;
          try {
            const saved = localStorage.getItem("whatsapp_new_customer_template");
            if (saved) newCustomerTemplate = saved;
          } catch { /* ignore */ }
          fetch(`${supabaseUrl}/functions/v1/whatsapp-notify`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${supabaseAnonKey}` },
            body: JSON.stringify({ type: "new_customer", name: name.trim(), phone, newCustomerTemplate }),
          }).catch(() => {});
        }
      } catch { /* ignore */ }

      return data;
    } catch {
      return null;
    }
  }, [getCustomerByPhone]);

  const updateCustomerName = useCallback(async (phone: string, name: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from("customers").update({ name }).eq("phone", phone);
      return !error;
    } catch {
      return false;
    }
  }, []);

  const updateCustomerPhone = useCallback(async (customerId: string, phone: string): Promise<{ error: string | null }> => {
    try {
      const cleaned = phone.replace(/[\s\-]/g, "");
      if (!/^(05\d{8}|009665\d{8}|\+9665\d{8})$/.test(cleaned)) {
        return { error: "يرجى إدخال رقم جوال سعودي صحيح (05xxxxxxxx)" };
      }
      let normalized = cleaned;
      if (cleaned.startsWith("+9665")) normalized = "0" + cleaned.slice(4);
      else if (cleaned.startsWith("009665")) normalized = "0" + cleaned.slice(5);

      const { data: existing } = await supabase.from("customers").select("id").eq("phone", normalized).maybeSingle();
      if (existing && existing.id !== customerId) {
        return { error: "رقم الجوال هذا مستخدم من قِبل حساب آخر" };
      }

      const { error } = await supabase.from("customers").update({ phone: normalized }).eq("id", customerId);
      if (!error) {
        setUser((prev) => {
          if (!prev) return prev;
          const updated = { ...prev, phone: normalized };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
          return updated;
        });
      }
      return { error: error ? "حدث خطأ أثناء الحفظ، حاول مرة أخرى" : null };
    } catch {
      return { error: "تعذّر الاتصال، تحقق من الإنترنت" };
    }
  }, []);

  const updateCustomerEmail = useCallback(async (customerId: string, email: string): Promise<{ error: string | null }> => {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const { data: existing } = await supabase.from("customers").select("id").eq("email", normalizedEmail).maybeSingle();
      if (existing && existing.id !== customerId) {
        return { error: "هذا البريد الإلكتروني مستخدم من قِبل حساب آخر" };
      }
      const { error } = await supabase.from("customers").update({ email: normalizedEmail }).eq("id", customerId);
      if (!error) {
        setUser((prev) => {
          if (!prev) return prev;
          const updated = { ...prev, email: normalizedEmail };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
          return updated;
        });
      }
      return { error: error ? "حدث خطأ أثناء الحفظ، حاول مرة أخرى" : null };
    } catch {
      return { error: "تعذّر الاتصال، تحقق من الإنترنت" };
    }
  }, []);

  const updateCustomerAddress = useCallback(async (phoneOrId: string, city: string, address: string): Promise<boolean> => {
    try {
      let error: { message: string } | null = null;

      if (isRealPhone(phoneOrId)) {
        // Phone-based user
        const result = await supabase.from("customers").update({ city, address }).eq("phone", phoneOrId);
        error = result.error;
      } else {
        // Email-only user — update by id
        const result = await supabase.from("customers").update({ city, address }).eq("id", phoneOrId);
        error = result.error;
      }

      if (!error) {
        // Always update local state & localStorage immediately
        setUser((prev) => {
          if (!prev) return prev;
          const updated = { ...prev, city, address };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
          return updated;
        });
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      session: null,
      login,
      logout,
      isLoggedIn: !!user,
      sendOtp,
      verifyOtp,
      loginOrRegisterWithEmail,
      findOrCreateCustomer,
      getCustomerByPhone,
      updateCustomerName,
      updateCustomerAddress,
      updateCustomerEmail,
      updateCustomerPhone,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
